import React, { useState, useEffect } from 'react';
import axiosPost from '../helpers/axios-helper';
import '../styles/Blog.css';

/**
 * Renders an editable site page by slug — the same block structure articles
 * use, so the dashboard's Site Pages module is what changes the content.
 * Our Story and About Us are both just rows in site_pages.
 */
export default function SitePage({ slug = 'our-story' }) {
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const closeLightbox = () => setLightboxOpen(false);
    const showPrev = () => setLightboxIndex((i) => (i - 1 + lightboxImages.length) % lightboxImages.length);
    const showNext = () => setLightboxIndex((i) => (i + 1) % lightboxImages.length);

    // Keyboard navigation for lightbox
    useEffect(() => {
        if (!lightboxOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowLeft') showPrev();
            if (e.key === 'ArrowRight') showNext();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightboxOpen, lightboxImages.length]);

    // Render blocks like Blog.js
    const renderBlocks = (blocks) => {
        if (!Array.isArray(blocks)) return null;
        return blocks.map((b, idx) => {
            if (b.type === 'header') {
                return (
                    <h2 key={idx} className="blog-block-header">
                        {b.content}
                    </h2>
                );
            }
            if (b.type === 'image') {
                const c = b.content;
                if (typeof c === 'string') {
                    return (
                        <div key={idx} className="blog-image-item">
                            <img
                                src={c}
                                alt=""
                                className="blog-block-image blog-image-clickable"
                                onClick={() => {
                                    setLightboxImages([c]);
                                    setLightboxIndex(0);
                                    setLightboxOpen(true);
                                }}
                            />
                        </div>
                    );
                }
                if (c && Array.isArray(c.images)) {
                    const images = c.images;
                    return (
                        <div key={idx} className={images.length > 1 ? 'blog-images-grid' : ''}>
                            {images.map((im, i) => (
                                <div key={`img-${idx}-${i}`} className="blog-image-item">
                                    <img
                                        src={im.url}
                                        alt={im.caption || ''}
                                        className="blog-block-image blog-image-clickable"
                                        onClick={() => {
                                            const urls = images.map(m => m.url);
                                            setLightboxImages(urls);
                                            setLightboxIndex(i);
                                            setLightboxOpen(true);
                                        }}
                                    />
                                    {im.caption && (
                                        <div className="blog-image-caption">{im.caption}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                }
                return null;
            }
            if (b.type === 'video') {
                const c = b.content;
                if (c && c.url) {
                    const formatVideoUrl = (url) => {
                        if (url.includes('youtube.com/watch')) {
                            const videoId = new URL(url).searchParams.get('v');
                            if (videoId) return `https://www.youtube.com/embed/${videoId}`;
                        }
                        if (url.includes('youtu.be')) {
                            const videoId = url.split('/').pop().split('?')[0];
                            if (videoId) return `https://www.youtube.com/embed/${videoId}`;
                        }
                        if (url.includes('vimeo.com')) {
                            const videoId = url.split('/').pop().split('?')[0];
                            if (videoId) return `https://player.vimeo.com/video/${videoId}`;
                        }
                        return url;
                    };
                    return (
                        <div key={idx} className="blog-video-container">
                            <iframe
                                src={formatVideoUrl(c.url)}
                                title={c.caption || 'Video'}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                            {c.caption && (
                                <div className="blog-image-caption">{c.caption}</div>
                            )}
                        </div>
                    );
                }
                return null;
            }
            if (b.type === 'text') {
                const c = b.content;
                if (typeof c === 'string') {
                    // Check if it's HTML content
                    if (c.startsWith('<') || c.includes('<p>') || c.includes('<h')) {
                        return (
                            <div 
                                key={idx} 
                                className="blog-block-html"
                                dangerouslySetInnerHTML={{ __html: c }}
                            />
                        );
                    }
                    // Plain text
                    return (
                        <p key={idx} className="blog-block-paragraph">
                            {c}
                        </p>
                    );
                }
            }
            return null;
        });
    };

    useEffect(() => {
        axiosPost(
            'site-pages/get',
            { 'Content-Type': 'application/json' },
            { slug },
            (resp) => {
                if (resp?.success && resp?.data) {
                    setPage(resp.data);
                } else {
                    setPage(null);
                }
                setLoading(false);
            },
            (err) => {
                console.error('Failed to load site page:', err);
                setPage(null);
                setLoading(false);
            }
        );
        // Refetch when the slug changes, so moving between Our Story and
        // About Us does not leave the previous page's content on screen.
    }, [slug]);

    const formatDate = (unix) => {
        if (!unix) return '';
        try { return new Date(unix * 1000).toLocaleDateString(); } catch { return ''; }
    };

    if (loading) return <div className="blog-loading">Loading...</div>;
    if (!page) return <div className="blog-not-found">Page not found.</div>;

    const hasBlocks = page.blocks && Array.isArray(page.blocks) && page.blocks.length > 0;

    return (
        <div className="blog-container" style={{ paddingTop: '110px' }}>
            {/* Hero Image */}
            {page.featured_image_url && (
                <img
                    src={page.featured_image_url}
                    alt={page.title}
                    className="blog-hero"
                />
            )}

            {/* Title */}
            <h1 className="blog-title">{page.title}</h1>

            {/* Meta */}
            <div className="blog-meta">
                {page.updated_at && <span>{formatDate(page.updated_at)}</span>}
            </div>

            {/* Excerpt */}
            {page.excerpt && (
                <div className="blog-excerpt">{page.excerpt}</div>
            )}

            {/* Content / Blocks */}
            <div className="blog-blocks">
                {hasBlocks ? renderBlocks(page.blocks) : (
                    <p className="blog-block-paragraph">Content coming soon...</p>
                )}
            </div>

            {/* Lightbox Overlay */}
            {lightboxOpen && lightboxImages.length > 0 && (
                <div className="lightbox-backdrop" onClick={closeLightbox}>
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">×</button>
                        {lightboxImages.length > 1 && (
                            <>
                                <button className="lightbox-nav lightbox-prev" onClick={showPrev} aria-label="Previous">‹</button>
                                <button className="lightbox-nav lightbox-next" onClick={showNext} aria-label="Next">›</button>
                            </>
                        )}
                        <img src={lightboxImages[lightboxIndex]} alt="" className="lightbox-image" />
                    </div>
                </div>
            )}
        </div>
    );
}
